/**
 * Day-by-day newborn feeding expectations, from the FFP prenatal handbook's
 * "What to expect after delivery" table plus Nicki's figures.
 *
 * ⚠ CLINICAL CONTENT — any change here needs sign-off from the FFP
 * lactation team (Nicki) before release. Numbers are minimums/typical
 * ranges for a healthy term newborn, not diagnostic thresholds.
 */

export interface DayExpectation {
  /** day of life; day 1 = birth day */
  day: number;
  /** minimum wet diapers expected in that 24h */
  wetMin: number;
  /** minimum stools expected in that 24h */
  dirtyMin: number;
  feedsMin: number;
  feedsMax: number;
  /** typical per-feed intake in ml, when bottle amounts apply */
  intakeMlMin: number;
  intakeMlMax: number;
  /** i18n key under content.stages.* describing this stage */
  stageKey: 'colostrum' | 'transitional' | 'milkIn';
}

/** Up to 7% weight loss from birth weight is within normal range. */
export const MAX_NORMAL_WEIGHT_LOSS_PCT = 7;
/** Babies are expected back at birth weight by two weeks. */
export const BIRTH_WEIGHT_REGAIN_DAY = 14;
/** Milk typically transitions ("comes in") around day 3 to 5. */
export const MILK_IN_DAY_RANGE: [number, number] = [3, 5];

const TABLE: Omit<DayExpectation, 'day'>[] = [
  // day 1 — colostrum in teaspoons; ~5-7 ml per feed
  { wetMin: 1, dirtyMin: 1, feedsMin: 8, feedsMax: 12, intakeMlMin: 5, intakeMlMax: 7, stageKey: 'colostrum' },
  // day 2
  { wetMin: 2, dirtyMin: 1, feedsMin: 8, feedsMax: 12, intakeMlMin: 5, intakeMlMax: 15, stageKey: 'colostrum' },
  // day 3 — transitional; in-hospital up to ~1 oz per feed
  { wetMin: 3, dirtyMin: 2, feedsMin: 8, feedsMax: 12, intakeMlMin: 15, intakeMlMax: 30, stageKey: 'transitional' },
  // day 4
  { wetMin: 4, dirtyMin: 3, feedsMin: 8, feedsMax: 12, intakeMlMin: 30, intakeMlMax: 60, stageKey: 'transitional' },
  // day 5 — milk in; ~1-3 oz per feed
  { wetMin: 6, dirtyMin: 3, feedsMin: 8, feedsMax: 12, intakeMlMin: 30, intakeMlMax: 90, stageKey: 'milkIn' },
];

/** day 6 onward */
const STEADY: Omit<DayExpectation, 'day'> = {
  wetMin: 6,
  dirtyMin: 3,
  feedsMin: 8,
  feedsMax: 12,
  intakeMlMin: 30,
  intakeMlMax: 90,
  stageKey: 'milkIn',
};

export function expectationsFor(dayOfLife: number): DayExpectation {
  const day = Math.max(1, Math.floor(dayOfLife));
  const row = TABLE[day - 1] ?? STEADY;
  return { day, ...row };
}
